if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        this.initialCheckOptions(); // Check if any options are already selected
        this.initQuantityHandlers();
        this.initMobileOptions();
        this.initStickyButton();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.cleanupStickyButton();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // On mobile, check if all options are selected before proceeding
        if (window.matchMedia('(max-width: 749px)').matches) {
          const allSelected = this.areAllOptionsSelected();
          
          if (!allSelected) {
            // Preserve radio checked states - don't let them get reset
            const checkedRadios = {};
            this.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
              checkedRadios[radio.id] = true;
            });
            
            // Update labels
            const options = this.querySelectorAll(".product-form__input");
            options.forEach((option) => {
              const labelSpan = option.querySelector('[data-selected-value]');
              if (!labelSpan) return;

              let selectedValue = '';
              if (option.classList.contains('product-form__input--dropdown')) {
                const select = option.querySelector('select');
                if (select && select.selectedIndex >= 0) {
                  const selectedOption = select.options[select.selectedIndex];
                  // Check if it's not the placeholder
                  if (selectedOption && selectedOption.value && selectedOption.value !== '' && 
                      !selectedOption.textContent.toLowerCase().includes('choose')) {
                    selectedValue = selectedOption.value;
                  }
                }
              } else {
                const checked = option.querySelector('input[type="radio"]:checked');
                if (checked) {
                  selectedValue = checked.value;
                  // Ensure checked attribute is set
                  checked.setAttribute('checked', 'checked');
                }
              }

              labelSpan.innerHTML = selectedValue || 'Choose an option';
            });
            
            // Restore any radio states that might have been lost
            Object.keys(checkedRadios).forEach(radioId => {
              const radio = this.querySelector(`input[type="radio"]#${radioId}`);
              if (radio && !radio.checked) {
                radio.checked = true;
                radio.setAttribute('checked', 'checked');
              }
            });
            

            // This way greyed-out states only appear after user has selected all options
            const allSelected = this.areAllOptionsSelected();
            if (allSelected) {
              setTimeout(() => {
                this.disableSwatch();
              }, 100);
            } else {
              // Remove greyed-out states if not all options are selected
              this.querySelectorAll('.greyed-out').forEach(el => {
                el.classList.remove('greyed-out');
              });
              // Clean dropdown option text
              this.querySelectorAll('.product-form__input--dropdown select option').forEach(opt => {
                if (opt.hasAttribute('data-placeholder') || !opt.value || opt.value === '') return;
                const originalText = opt.getAttribute('data-original-text');
                if (originalText) {
                  opt.textContent = originalText;
                }
              });
            }
            
            // Keep button with "Choose Options" or "Sold Out" (use pointer-events instead of disabled)
            const submitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
            if (submitButton) {
              const hasAvailable = this.hasAvailableVariants();
              const buttonSpan = submitButton.querySelector('span');
              
              if (hasAvailable) {
                // Has available variants - show "Choose Options"
                submitButton.removeAttribute('disabled');
                submitButton.classList.add('button--choose-options');
                if (buttonSpan) {
                  buttonSpan.textContent = 'Choose Options';
                }
              } else {
                // All variants sold out - show "Sold Out"
                submitButton.setAttribute('disabled', 'disabled');
                submitButton.classList.remove('button--choose-options');
                if (buttonSpan && window.variantStrings?.soldOut) {
                  buttonSpan.textContent = window.variantStrings.soldOut;
                }
              }
            }
            
            // Clear variant input
            this.updateVariantInputs('');
            return;
          }
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, event, target),
        });

      }

      // Check if any variant has available quantity
      hasAvailableVariants() {
        if (!window.variantData?.length) return false;
        
        const options = this.querySelectorAll(".product-form__input");
        if (options.length === 0) return true; // No options means single variant product
        
        // Check if any variant has quantity > 0 and is visible on frontend
        return window.variantData.some(variant => {
          const inventoryQty = parseInt(variant.inventory_quantity) || 0;
          const showOnFe = variant.show_on_fe !== 'false';
          return inventoryQty > 0 && showOnFe;
        });
      }

      // Initialize mobile options (remove preselects, set button to "Choose Options")
      initMobileOptions() {
        if (!window.matchMedia('(max-width: 768px)').matches) return;
        
        const options = this.querySelectorAll(".product-form__input");
        if (options.length === 0) return; // No options, skip

        // Remove all preselects
        options.forEach((option) => {
          if (option.classList.contains('product-form__input--dropdown')) {
            const select = option.querySelector('select');
            if (select) {
              // Check if first option is already a placeholder
              const firstOption = select.options[0];
              const hasPlaceholder = firstOption && (!firstOption.value || firstOption.value === '' || firstOption.textContent.toLowerCase().includes('choose'));
              
              if (!hasPlaceholder) {
                // Add placeholder option as first option
                const placeholder = document.createElement('option');
                placeholder.value = '';
                placeholder.textContent = 'Choose an option';
                placeholder.selected = true;
                placeholder.setAttribute('data-placeholder', 'true');
                select.insertBefore(placeholder, select.firstChild);
              } else {
                // Select the placeholder option
                Array.from(select.options).forEach(opt => opt.selected = false);
                if (firstOption) {
                  firstOption.selected = true;
                  if (!firstOption.hasAttribute('data-placeholder')) {
                    firstOption.setAttribute('data-placeholder', 'true');
                  }
                }
              }
            }
          } else {
            option.querySelectorAll('input[type="radio"]').forEach(radio => {
              radio.checked = false;
            });
          }
        });

        // Clear variant input
        this.updateVariantInputs('');
        
        // Set button to "Choose Options" or "Sold Out" (use pointer-events instead of disabled)
        const submitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        if (submitButton) {
          const hasAvailable = this.hasAvailableVariants();
          const buttonSpan = submitButton.querySelector('span');
          
          if (hasAvailable) {
            // Has available variants - show "Choose Options"
            submitButton.removeAttribute('disabled');
            submitButton.classList.add('button--choose-options');
            if (buttonSpan) {
              buttonSpan.textContent = 'Choose Options';
            }
          } else {
            // All variants sold out - show "Sold Out"
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.classList.remove('button--choose-options');
            if (buttonSpan && window.variantStrings?.soldOut) {
              buttonSpan.textContent = window.variantStrings.soldOut;
            }
          }
        }

        // Update labels to "Choose an option"
        options.forEach((option) => {
          const labelSpan = option.querySelector('[data-selected-value]');
          if (labelSpan) {
            labelSpan.innerHTML = 'Choose an option';
          }
        });

        // Don't show greyed out states on initial load - only after user starts selecting
        // Remove any existing greyed-out classes
        this.querySelectorAll('.greyed-out').forEach(el => {
          el.classList.remove('greyed-out');
        });
        
        // Reset dropdown option text (remove "- Unavailable" or "- Sold Out")
        options.forEach((option) => {
          if (option.classList.contains('product-form__input--dropdown')) {
            const select = option.querySelector('select');
            if (select) {
              Array.from(select.options).forEach(opt => {
                // Skip placeholder
                if (opt.hasAttribute('data-placeholder') || !opt.value || opt.value === '') return;
                // Restore original text if stored
                const originalText = opt.getAttribute('data-original-text');
                if (originalText) {
                  opt.textContent = originalText;
                } else {
                  // Store current text and clean it
                  const currentText = opt.textContent;
                  const cleanedText = currentText.replace(/\s*-\s*(Unavailable|Sold Out)$/i, '').trim();
                  opt.textContent = cleanedText;
                  opt.setAttribute('data-original-text', cleanedText);
                }
              });
            }
          }
        });
      }

      // Check if user has made any selection (not just placeholders)
      hasAnySelection() {
        const options = this.querySelectorAll(".product-form__input");
        for (const option of options) {
          if (option.classList.contains('product-form__input--dropdown')) {
            const select = option.querySelector('select');
            if (select && select.selectedIndex > 0) {
              const selectedOption = select.options[select.selectedIndex];
              // Check if it's not the placeholder
              if (selectedOption && selectedOption.value && selectedOption.value !== '' && 
                  !selectedOption.textContent.toLowerCase().includes('choose')) {
                return true;
              }
            }
          } else {
            if (option.querySelector('input[type="radio"]:checked')) {
              return true;
            }
          }
        }
        return false;
      }

      // Check if all options are selected
      areAllOptionsSelected() {
        const options = this.querySelectorAll(".product-form__input");
        if (options.length === 0) return true;

        for (const option of options) {
          if (option.classList.contains('product-form__input--dropdown')) {
            const select = option.querySelector('select');
            if (!select) return false;
            const selectedOption = select.options[select.selectedIndex];
            // Check if placeholder is selected (empty value or "Choose an option" text)
            if (!selectedOption || !selectedOption.value || selectedOption.value === '' || 
                selectedOption.textContent.toLowerCase().includes('choose')) {
              return false;
            }
          } else {
            if (!option.querySelector('input[type="radio"]:checked')) return false;
          }
        }
        return true;
      }

      // Check if any options are already selected
      initialCheckOptions() {
        const options = document.querySelectorAll(".product-form__input");
        let defaultChecked;

        // Skip preselecting on mobile if product has options
        if (options.length > 0 && window.matchMedia('(max-width: 768px)').matches) {
          return;
        }

        if (options.length > 0) {
          const selectedValues = {};
          options.forEach((option, index) => {
            if (option.classList.contains('product-form__input--dropdown')) {
              const select = option.querySelector('select');
              const selected = Array.from(select.children).find(opt => opt.selected);
              selectedValues[`option${index + 1}`] = selected?.value?.toLowerCase();
            } else {
              const checked = option.querySelector('input[type="radio"]:checked');
              selectedValues[`option${index + 1}`] = checked?.value?.toLowerCase();
            }
          });

          const currentVariant = window.variantData.find(variant => {
            return Object.keys(selectedValues).every(key =>
              variant[key]?.toLowerCase() === selectedValues[key]
            );
          });

          if (!currentVariant || currentVariant.show_on_fe === 'false') {
            const firstAvailableVariant = window.variantData.find(v => v.show_on_fe !== 'false');

            if (firstAvailableVariant) {
              // Track selected elements for each option
              const selectedElements = [];

              // Update UI selections and collect selected elements
              options.forEach((option, index) => {
                const optionValue = firstAvailableVariant[`option${index + 1}`];

                if (option.classList.contains('product-form__input--dropdown')) {
                  const select = option.querySelector('select');
                  const optionToSelect = Array.from(select.children)
                    .find(opt => opt.value.toLowerCase() === optionValue?.toLowerCase());

                  if (optionToSelect) {
                    optionToSelect.selected = true;
                    selectedElements[index] = optionToSelect;
                  }
                } else {
                  const radioToCheck = option.querySelector(
                    `input[type="radio"][value="${optionValue}"]`
                  );

                  if (radioToCheck) {
                    radioToCheck.checked = true;
                    selectedElements[index] = radioToCheck;
                    defaultChecked = radioToCheck;
                  }
                }
              });

              // Update variant ID and form
              this.updateVariantInputs(firstAvailableVariant.id);

              // Force price update for the new variant
              const productUrl = this.dataset.url;
              this.renderProductInfo({
                requestUrl: `${productUrl}?variant=${firstAvailableVariant.id}&section_id=${this.dataset.section}`,
                targetId: defaultChecked?.id || '',
                callback: this.handleUpdateProductInfo(productUrl, {
                  target: defaultChecked
                }, defaultChecked)
              });

              // Update button state
              const submitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
              if (submitButton) {
                submitButton.disabled = false;
                submitButton.classList.remove('hidden', 'button--choose-options');

                const buttonSpan = submitButton.querySelector('span');
                if (buttonSpan) {
                  buttonSpan.textContent = window.variantStrings.addToCart;
                }
                submitButton.removeAttribute('data-show-fe');
              }

              // Update product form
              const productForm = this.productForm;
              if (productForm) {
                productForm.classList.remove('hidden');
                productForm.dispatchEvent(new CustomEvent('variant:changed', {
                  detail: {
                    variant: firstAvailableVariant
                  }
                }));
              }

              // Trigger change events sequentially
              selectedElements.forEach((element, index) => {
                if (element) {
                  const customEvent = new CustomEvent('change', {
                    bubbles: true,
                    detail: {
                      originalTarget: element
                    }
                  });
                  // Dispatch from the element itself
                  element.dispatchEvent(customEvent);
                }
              });

              // Update swatches for all options
              options.forEach((option, index) => {
                this.disableSwatch(selectedElements[index], index);
              });
            }
          } else {
            // Update swatches for current selection
            this.disableSwatch(defaultChecked, 0);
          }
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // On mobile, preserve radio checked states before updating
          let preservedRadioStates = {};
          if (window.matchMedia('(max-width: 768px)').matches) {
            this.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
              preservedRadioStates[radio.id] = true;
            });
          }
          
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          
          // Restore radio checked states after update
          if (window.matchMedia('(max-width: 768px)').matches) {
            Object.keys(preservedRadioStates).forEach(radioId => {
              const radio = this.querySelector(`input[type="radio"]#${radioId}`);
              if (radio) {
                radio.checked = true;
                radio.setAttribute('checked', 'checked');
              }
            });
          }
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          setTimeout(() => {
            this.disableSwatch();
          }, 800);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );


          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      async disableSwatch() {
        // Wait for next frame to ensure variant data is updated
        await new Promise(resolve => requestAnimationFrame(resolve));

        if (!this.hasValidVariantData()) return;

        const options = this.querySelectorAll(".product-form__input");
        const selectedOptions = this.getSelectedOptions(options);

        options.forEach((option, index) => {
          this.updateOptionAvailability(option, index + 1, selectedOptions);
        });
      }

      hasValidVariantData() {
        if (!window.variantData?.length) {
          console.error('Variant data not available yet');
          return false;
        }
        return true;
      }

      getSelectedOptions(options) {
        const selectedOptions = {};
        options.forEach((option, index) => {
          const elements = this.checkOptionType(option);
          const selectedElement = Array.from(elements).find(el => el.checked || el.selected);
          if (selectedElement) {
            // Ensure checked attribute is set for radio inputs
            if (selectedElement.type === 'radio' && selectedElement.checked && !selectedElement.hasAttribute('checked')) {
              selectedElement.setAttribute('checked', 'checked');
            }
            selectedOptions[`option${index + 1}`] = selectedElement.value.toLowerCase();
          }
        });
        return selectedOptions;
      }

      updateOptionAvailability(option, optionIndex, selectedOptions) {
        const elements = this.checkOptionType(option);
        Array.from(elements).forEach(element => {
          const variantsForOption = this.findMatchingVariants(element, optionIndex, selectedOptions);
          this.updateElementStatus(element, variantsForOption);
        });
      }

      findMatchingVariants(element, optionIndex, selectedOptions) {
        const testOptions = { ...selectedOptions };
        const elementValue = (element instanceof HTMLOptionElement)
          ? element.value
          : element.getAttribute('value');
        testOptions[`option${optionIndex}`] = elementValue.toLowerCase();

        return window.variantData.filter(variant =>
          Object.keys(testOptions).every(key =>
            variant[key]?.toLowerCase() === testOptions[key]
          )
        );
      }

      updateElementStatus(element, variants) {
        const isSelected = element.selected || element.checked;
        const isAvailable = variants.length > 0;
        const hasVisibleVariants = variants.some(v => v.show_on_fe !== 'false');
        const hasSoldOutVariants = variants.every(v => v.inventory_quantity <= 0);

        if (element instanceof HTMLOptionElement) {
          this.updateDropdownOption(element, isAvailable, hasVisibleVariants, isSelected, hasSoldOutVariants);
        } else {
          this.updateSwatchLabel(element, isAvailable, hasVisibleVariants, hasSoldOutVariants, hasSoldOutVariants);
        }
      }

      updateDropdownOption(option, isAvailable, hasVisibleVariants, isSelected, isSoldOut) {
        // Skip placeholder options - don't modify their text
        if (option.hasAttribute('data-placeholder') || !option.value || option.value === '') {
          if (isSelected) option.selected = true;
          return;
        }

        // Store original text if not already stored
        if (!option.hasAttribute('data-original-text')) {
          option.setAttribute('data-original-text', option.textContent);
        }
        
        const originalValue = option.getAttribute('data-original-text') || option.value;

        if (!isAvailable || !hasVisibleVariants) {
          option.textContent = `${originalValue} - Unavailable`;
        } else if (isSoldOut) {
          option.textContent = `${originalValue} - Sold Out`;
        } else {
          option.textContent = originalValue;
        }

        if (isSelected) option.selected = true;
      }

      updateSwatchLabel(swatch, isAvailable, hasVisibleVariants, hasSoldOutVariants) {
        const label = this.querySelector(`label[for="${swatch.id}"]`);
        if (label) {
          label.classList.remove("greyed-out");
          if (!isAvailable || !hasVisibleVariants || hasSoldOutVariants) {
            label.classList.add("greyed-out");
          }
        }
      }

      checkOptionType(option) {
        let type;
        if (option.classList.contains('product-form__input--dropdown')) {
          const firstSelect = option.querySelector('select');
          type = firstSelect.querySelectorAll('option');
        } else {
          type = option.querySelectorAll('input[type="radio"]');
        }
        return type;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      // Initialize sticky button for mobile
      initStickyButton() {
        if (!window.matchMedia('(max-width: 768px)').matches) return;

        const originalButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const buttonsContainer = this.querySelector('.product-form__buttons');
        if (!originalButton || !buttonsContainer) return;

        // Create sticky wrapper and clone button
        const stickyWrapper = document.createElement('div');
        stickyWrapper.className = 'product-form__sticky-button-wrapper';
        stickyWrapper.appendChild(buttonsContainer.cloneNode(true));
        document.body.appendChild(stickyWrapper);

        // Handle sticky button click
        const stickyButton = stickyWrapper.querySelector('button[type="submit"]');
        // HELP-13922: the Yanet "Notify me when available" button is injected into
        // .product-form__buttons after this clone is made, so we mirror and trigger
        // the real one instead of the inert "Sold out" clone on sold-out products.
        const realNotifyButton = () => this.querySelector('bis-inline');
        if (stickyButton) {
          stickyButton.addEventListener('click', (e) => {
            const buttonSpan = stickyButton.querySelector('span');
            // Sold-out product with a Notify-me button: trigger the real Yanet flow
            if (stickyButton.dataset.notifyMode === 'true') {
              e.preventDefault();
              e.stopPropagation();
              realNotifyButton()?.click();
              return;
            }
            // If button says "Choose Options", scroll to options instead of submitting
            if (buttonSpan && buttonSpan.textContent.trim() === 'Choose Options') {
              e.preventDefault();
              e.stopPropagation();
              // Find the first product option input
              const firstOption = this.querySelector('.product-form__input, variant-selects');
              if (firstOption) {
                const elementTop = firstOption.getBoundingClientRect().top + window.pageYOffset;
                const offset = 100; // 100px from top
                window.scrollTo({
                  top: elementTop - offset,
                  behavior: 'smooth'
                });
              }
            } else if (this.productForm) {
              // Otherwise submit the form
              this.productForm.querySelector('form')?.requestSubmit();
            }
          });
        }

        // Sync sticky button with original button
        const syncStickyButton = () => {
          if (!stickyButton || !originalButton) return;

          // HELP-13922: sold-out products expose a Yanet "Notify me when available"
          // button next to the (inert, disabled) Sold out submit. When present and
          // visible, mirror it in the sticky bar so the CTA is actionable.
          const notifyButton = realNotifyButton();
          const notifyMode = !!notifyButton
            && notifyButton.offsetParent !== null
            && originalButton.hasAttribute('disabled');
          if (notifyMode) {
            const stickySpanEl = stickyButton.querySelector('span');
            const notifyText = notifyButton.querySelector('span')?.textContent?.trim()
              || notifyButton.textContent.trim();
            if (stickySpanEl && notifyText) stickySpanEl.textContent = notifyText;
            stickyButton.dataset.notifyMode = 'true';
            stickyButton.removeAttribute('disabled');
            stickyButton.removeAttribute('aria-disabled');
            stickyButton.classList.remove('button--choose-options');
            stickyButton.querySelector('.loading__spinner')?.classList.add('hidden');
            return;
          }
          delete stickyButton.dataset.notifyMode;

          // Sync text content
          const originalSpan = originalButton.querySelector('span');
          const stickySpan = stickyButton.querySelector('span');
          if (originalSpan && stickySpan) {
            stickySpan.textContent = originalSpan.textContent;
          }
          
          // Sync disabled state
          if (originalButton.hasAttribute('disabled')) {
            stickyButton.setAttribute('disabled', 'disabled');
          } else {
            stickyButton.removeAttribute('disabled');
          }
          
          // Sync aria-disabled
          const ariaDisabled = originalButton.getAttribute('aria-disabled');
          if (ariaDisabled === 'true') {
            stickyButton.setAttribute('aria-disabled', 'true');
          } else {
            stickyButton.removeAttribute('aria-disabled');
          }
          
          // Sync classes (especially button--choose-options)
          stickyButton.className = originalButton.className;
          
          // Sync loading spinner
          const originalSpinner = originalButton.querySelector('.loading__spinner');
          const stickySpinner = stickyButton.querySelector('.loading__spinner');
          if (originalSpinner && stickySpinner) {
            if (originalSpinner.classList.contains('hidden')) {
              stickySpinner.classList.add('hidden');
            } else {
              stickySpinner.classList.remove('hidden');
            }
          }
        };

        // Watch for changes to original button
        const buttonObserver = new MutationObserver(syncStickyButton);
        buttonObserver.observe(originalButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true
        });

        // Also watch the span for text changes
        const originalSpan = originalButton.querySelector('span');
        if (originalSpan) {
          buttonObserver.observe(originalSpan, {
            childList: true,
            characterData: true
          });
        }

        // HELP-13922: the Yanet notify button is injected into the buttons container
        // after load and toggled per selected variant, so re-sync on container changes.
        buttonObserver.observe(buttonsContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        });

        // Initial sync
        syncStickyButton();

        // Handle scroll
        const handleScroll = () => {
          if (!window.matchMedia('(max-width: 768px)').matches) {
            stickyWrapper.classList.remove('is-visible');
            return;
          }

          const buttonRect = originalButton.getBoundingClientRect();
          stickyWrapper.classList.toggle('is-visible', buttonRect.top < 0);
        };

        this.scrollHandler = handleScroll;
        this.stickyWrapper = stickyWrapper;
        this.buttonObserver = buttonObserver;
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial check
      }

      // Cleanup sticky button
      cleanupStickyButton() {
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }
        if (this.buttonObserver) {
          this.buttonObserver.disconnect();
        }
        if (this.stickyWrapper) {
          this.stickyWrapper.remove();
        }
      }
    }
  );
}
